/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { EntityLoadingStatus } from '@backstage/plugin-catalog-react';

import {
  errorApiRef,
  useApi,
  useRouteRefParams,
} from '@backstage/frontend-plugin-api';
import { catalogApiRef, entityRouteRef } from '@backstage/plugin-catalog-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAsyncRetry from 'react-use/esm/useAsyncRetry';

/**
 * @deprecated TODO can be removed after https://github.com/backstage/backstage/pull/31128 is released
 */
export const useEntityFromUrl = (): EntityLoadingStatus => {
  const { kind, name, namespace } = useRouteRefParams(entityRouteRef);
  const navigate = useNavigate();
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);

  const {
    error,
    loading,
    retry: refresh,
    value: entity,
  } = useAsyncRetry(
    () => catalogApi.getEntityByRef({ kind, name, namespace }),
    [catalogApi, kind, namespace, name],
  );

  useEffect(() => {
    if (name) {
      return;
    }

    errorApi.post(new Error('No name provided!'));
    navigate('/');
  }, [errorApi, navigate, error, loading, entity, name]);

  return { entity, error, loading, refresh };
};
